import React, { useEffect, useState } from 'react';
import { Search, Users, ClipboardList, ShieldAlert, ArrowRight } from 'lucide-react';
import { api, DEMO } from '../lib/api';
import { hasCap } from '../lib/capabilities';
import './PhpWorkQueue.css';

function flags(it) {
  const out = [];
  if (it.open_escalations > 0) out.push({ tone: 'safety', label: 'Escalation' });
  if (it.recertification?.overdue) out.push({ tone: 'fail', label: 'Recert overdue' });
  if (it.requires_initial_certification) out.push({ tone: 'fail', label: 'Needs certification' });
  if (it.unsigned_treatment_plan) out.push({ tone: 'pending', label: 'Unsigned plan' });
  if (it.unsigned_services > 0) out.push({ tone: 'pending', label: `${it.unsigned_services} unsigned` });
  if (it.open_safety_assessments > 0) out.push({ tone: 'safety', label: 'Safety follow-up' });
  return out;
}

export default function PhpWorkQueue({ user, facility, navigate, notify, store }) {
  const [data, setData] = useState(null), [loading, setLoading] = useState(true), [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (DEMO) { setLoading(false); return; }
    let active = true;
    api('/php/work-queue')
      .then(d => { if (active) setData(d); })
      .catch(e => { if (active) setError(e.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const queue = DEMO ? [] : (data?.queue || []);
  const myQueue = DEMO ? [] : (data?.my_queue || []);

  const filtered = queue.filter(it => {
    const q = query.toLowerCase();
    const matchQ = !q || `${it.name} ${it.mrn || ''} ${it.treating_physician || ''}`.toLowerCase().includes(q);
    if (!matchQ) return false;
    if (filter === 'all') return true;
    if (filter === 'mine') return myQueue.some(m => m.enrollment_id === it.enrollment_id);
    if (filter === 'attention') return flags(it).length > 0;
    if (filter === 'recert') return it.recertification?.overdue || it.requires_initial_certification;
    if (filter === 'unsigned') return it.unsigned_treatment_plan || it.unsigned_services > 0;
    if (filter === 'safety') return it.open_escalations > 0 || it.open_safety_assessments > 0;
    return true;
  });

  const stats = [
    [ClipboardList, 'Active episodes', queue.length],
    [ShieldAlert, 'Needs attention', queue.filter(it => flags(it).length > 0).length],
    [Users, 'My patients', myQueue.length],
  ];

  const openChart = p => navigate(`/patient/${p.patient_id}`);

  return <>
    <div className="page-heading"><div><div className="eyebrow">{facility?.name || 'YOUR FACILITY'} · PHP / IOP WORK QUEUE</div><h1>PHP program work queue, facility-wide.</h1><p>Active episodes that need action — certification, recertification, unsigned documentation, and safety follow-up — in one place.</p></div></div>

    {DEMO && <div className="php-safety-alert" style={{ borderColor: '#b45309', background: '#fffbeb', color: '#92400e' }}><div><strong>Work queue requires live data.</strong><p>The queue is computed server-side from enrollment, certification, treatment-plan, daily-service, and safety records, so it is available in the authenticated (non-demo) workspace.</p></div></div>}

    <div className="registry-stats">{stats.map(([Icon, label, n]) => <div key={label}><span className="stat-icon"><Icon size={21}/></span><div><strong>{n}</strong><span>{label}</span></div></div>)}</div>

    <section className="registry-panel practice-page">
      <div className="content-heading"><div><h3>Active PHP / IOP episodes <span className="badge neutral">{filtered.length}</span></h3><p>Ordered by urgency: escalations, overdue recertification, certification, then documentation.</p></div></div>
      <div className="registry-filters">
        <div className="filter-inline search-wide"><Search size={17}/><input aria-label="Search work queue" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search patient name, MRN, or physician…"/></div>
        <select aria-label="Filter" value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="all">All active</option>
          <option value="mine">My patients</option>
          <option value="attention">Needs attention</option>
          <option value="recert">Certification / recert</option>
          <option value="unsigned">Unsigned documentation</option>
          <option value="safety">Safety</option>
        </select>
      </div>

      {loading ? <div className="generation-skeleton"><i/><i/><i/></div> : error ? <div className="inline-alert danger" role="alert">{error}</div> : !filtered.length ? <div className="empty-state"><Users size={28}/><h3>No episodes found</h3><p>No active PHP / IOP episodes match this filter.</p></div> : (
        <div className="table-scroll">
          <table>
            <thead><tr><th>Patient</th><th>MRN</th><th>Program</th><th>Day</th><th>Treating physician</th><th>Recertification</th><th>Plan</th><th>Needs action</th><th>Action</th></tr></thead>
            <tbody>
              {filtered.map(it => {
                const recert = it.recertification || {};
                const fl = flags(it);
                return <tr key={it.enrollment_id}>
                  <td><strong>{it.name}</strong></td>
                  <td>{it.mrn || '—'}</td>
                  <td>{it.program_type?.toUpperCase()}</td>
                  <td>{it.php_day != null ? `Day ${it.php_day}` : '—'}</td>
                  <td>{it.treating_physician || '—'}</td>
                  <td>{recert.overdue ? <span className="badge-danger badge">Overdue {recert.days_until_due != null ? `(${Math.abs(recert.days_until_due)}d)` : ''}</span> : it.requires_initial_certification ? <span className="badge-danger badge">Needs cert</span> : recert.upcoming ? <span className="badge-warn badge">Due soon</span> : recert.next_due_date ? <span className="badge-neutral badge">{recert.next_due_date}</span> : '—'}</td>
                  <td>{it.treatment_plan_status === 'active' ? <span className="php-status-badge pass">Signed</span> : <span className="php-status-badge pending">{it.treatment_plan_status || 'None'}</span>}</td>
                  <td>{fl.length ? fl.map(f => <span key={f.label} className={`flag-chip ${f.tone}`}>{f.label}</span>) : <span className="muted">Current</span>}</td>
                  <td>{hasCap(user, 'chart.view') && <button className="text-button" onClick={() => openChart(it)}>Open chart <ArrowRight size={13}/></button>}</td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  </>;
}
